import React, { useEffect } from 'react';
import { useSendFormStore, getCharacterCount, getRemainingCharacters, isCharacterWarning } from 'store/sendFormStore';

const MAX_MESSAGE_LENGTH = 160;

const SendForm: React.FC = () => {
  const {
    phoneNumber,
    message,
    isLoading,
    errors,
    lastResult,
    setPhoneNumber,
    setMessage,
    clearResult,
    sendSMS,
  } = useSendFormStore();

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(e.target.value);
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearResult();
    await sendSMS();
  };

  // Auto-dismiss success message after 5 seconds
  useEffect(() => {
    if (lastResult?.success) {
      const timer = setTimeout(() => {
        clearResult();
      }, 5000);

      return () => clearTimeout(timer);
    }
    return undefined; // No cleanup needed if no timer was set
  }, [lastResult, clearResult]);

  const isFormValid = phoneNumber.trim().length > 0 && message.trim().length > 0;
  const charCount = getCharacterCount(message);
  const remainingChars = getRemainingCharacters(message);
  const isCharWarning = isCharacterWarning(message);

  return (
    <div className="send-form">
      <div className="send-form__header">
        <h2>Send Message</h2>
        <p className="send-form__description">
          Compose and send SMS messages instantly
        </p>
      </div>

      <form onSubmit={handleSubmit} className="send-form__form">
        <div className="form-group">
          <label htmlFor="phoneNumber" className="form-label">
            Phone Number
          </label>
          <input
            type="tel"
            id="phoneNumber"
            value={phoneNumber}
            onChange={handlePhoneChange}
            className={`form-control ${errors.phone ? 'form-control--error' : ''}`}
            placeholder="+1234567890"
            disabled={isLoading}
            autoComplete="tel"
          />
          {errors.phone && (
            <div className="form-error" role="alert">
              {errors.phone}
            </div>
          )}
          <div className="form-help">
            Include country code (e.g., +1 for US/Canada)
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="message" className="form-label">
            Message
            <span className={`char-counter ${isCharWarning ? 'char-counter--warning' : ''}`}>
              {charCount}/{MAX_MESSAGE_LENGTH}
            </span>
          </label>
          <textarea
            id="message"
            value={message}
            onChange={handleMessageChange}
            className={`form-control form-control--textarea ${errors.message ? 'form-control--error' : ''}`}
            placeholder="Enter your SMS message..."
            rows={4}
            maxLength={MAX_MESSAGE_LENGTH}
            disabled={isLoading}
          />
          {errors.message && (
            <div className="form-error" role="alert">
              {errors.message}
            </div>
          )}
          {isCharWarning && remainingChars >= 0 && (
            <div className="form-warning">
              {remainingChars} characters remaining
            </div>
          )}
        </div>

        <button
          type="submit"
          className="send-button"
          disabled={!isFormValid || isLoading}
        >
          {isLoading ? (
            <>
              <span className="spinner spinner--small"></span>
              Sending...
            </>
          ) : (
            'Send Message'
          )}
        </button>

        {lastResult && (
          <div className={`send-result ${lastResult.success ? 'send-result--success' : 'send-result--error'}`} role="alert">
            <div className="send-result__icon">
              {lastResult.success ? '✅' : '❌'}
            </div>
            <div className="send-result__message">
              {lastResult.message}
            </div>
            <button
              type="button"
              className="send-result__close"
              onClick={clearResult}
              aria-label="Dismiss message"
              title="Dismiss message"
            >
              ✕
            </button>
          </div>
        )}
      </form>

      <div className="send-form__info">
        <h3>Message Details</h3>
        <ul>
          <li>Messages delivered via Twilio SMS platform</li>
          <li>Real-time delivery tracking and status updates</li>
          <li>Complete message history and logging</li>
          <li>Supports standard SMS and MMS formats</li>
        </ul>
      </div>
    </div>
  );
};

export default SendForm;